import React, { useState, useMemo, useEffect } from 'react';
import { useUserData } from '../../hooks/useUserData';
import ClientCard from '../common/ClientCard';
import ClientFilesView from './ClientFilesView';
import FilterBar from '../common/FilterBar'; // <-- Import FilterBar
import { PageTitle, ViewToggle, ToggleButton } from '../common/common.styles'; // <-- Import new styles
import { ClientGrid } from './DataEntryDashboard.styles';

const DataEntryDashboard = () => {
  const { users, documents, loading } = useUserData();
  
  // State for all controls
  const [selectedClient, setSelectedClient] = useState(null);
  const [viewMode, setViewMode] = useState('Active');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pending'); // Default to Pending in Active view
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // When switching views, reset the status filter
  useEffect(() => {
    setStatusFilter(viewMode === 'Active' ? 'Pending' : 'All');
  }, [viewMode]);

  // Updated logic to filter clients based on their documents' properties
  const filteredClients = useMemo(() => {
    const clients = users.filter(user => user.role === 'client');

    // Create a map of documents per user for efficient lookup
    const docsByUser = documents.reduce((acc, doc) => {
        (acc[doc.userId] = acc[doc.userId] || []).push(doc);
        return acc;
    }, {});

    return clients.filter(client => {
        const clientDocs = docsByUser[client.id] || [];
        if (clientDocs.length === 0) return false;

        // Check if ANY document from this client matches ALL active filters
        const hasMatchingDocs = clientDocs.some(doc => {
            const docDate = doc.uploadedAt.toDate();
            const matchesStatus = statusFilter === 'All' || doc.status === statusFilter;
            const matchesStartDate = !startDate || docDate >= startDate;
            const matchesEndDate = !endDate || docDate < new Date(endDate.getTime() + 24 * 60 * 60 * 1000);
            return matchesStatus && matchesStartDate && matchesEndDate;
        });
        
        if (!hasMatchingDocs) return false;

        // Finally, filter by Active/Archived view mode
        const hasPending = client.pendingCount > 0;
        if (viewMode === 'Active' && !hasPending) return false;
        if (viewMode === 'Archived' && hasPending) return false;

        return client.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [users, documents, searchTerm, viewMode, statusFilter, startDate, endDate]);

  if (loading) return <PageTitle>Loading data...</PageTitle>;

  if (selectedClient) {
    return <ClientFilesView client={selectedClient} documents={documents.filter(d => d.userId === selectedClient.id)} onBack={() => setSelectedClient(null)} />;
  }

  return (
    <div>
      <PageTitle>{viewMode} Clients</PageTitle>
      <ViewToggle>
        <ToggleButton active={viewMode === 'Active'} onClick={() => setViewMode('Active')}>Active</ToggleButton>
        <ToggleButton active={viewMode === 'Archived'} onClick={() => setViewMode('Archived')}>Archived</ToggleButton>
      </ViewToggle>

      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
      
      <ClientGrid>
        {filteredClients.map(client => (
          <ClientCard key={client.id} client={client} onClick={() => setSelectedClient(client)} />
        ))}
      </ClientGrid>
    </div>
  );
};

export default DataEntryDashboard;